import { useNavigate, useLocation } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import { Trophy, Users, FileText, Gift, BarChart3, UserCheck } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/auth/context/AuthContext'
import { kpiIconClasses } from '../lib/reporteColor'
import { esSoloVentasRegistros, TABS_VENTAS_REGISTROS } from '../lib/acceso'

const CONFIG_IDS_CON_PARTICIPANTES = [6, 7]
// La tab Representantes cruza el campo `representante` con el catálogo RRVV/RRMM
// del Excel; solo aplica a Medifarma Perú (id 7).
const CONFIG_IDS_CON_REPRESENTANTES = [7]

// Clases estáticas para que Tailwind no purgue el grid dinámico.
const GRID_COLS: Record<number, string> = {
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
}

interface SubNavItem {
  key: string
  label: string
  to: string
  icon: LucideIcon
}

interface Props {
  configId: number
  colorNombre: string
  colorTono: string
}

export function ReporteSubNav({ configId, colorNombre, colorTono }: Props) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const base = `/reportes-fidelizacion/${configId}`

  const tieneParticipantes = CONFIG_IDS_CON_PARTICIPANTES.includes(configId)
  const tieneRepresentantes = CONFIG_IDS_CON_REPRESENTANTES.includes(configId)
  const soloVentasRegistros = esSoloVentasRegistros(user)

  const items: SubNavItem[] = [
    { key: 'resumen', label: configId === 7 ? 'Reporte Gerencial' : 'Resumen Gerencial', to: base, icon: Trophy },
    { key: 'registros', label: 'Registros', to: `${base}/registros`, icon: Users },
    ...(tieneRepresentantes
      ? [{ key: 'representantes', label: 'Representantes', to: `${base}/representantes`, icon: UserCheck }]
      : []),
    ...(tieneParticipantes
      ? [{ key: 'participantes', label: 'Participantes', to: `${base}/participantes`, icon: BarChart3 }]
      : []),
    { key: 'ventas', label: 'Ventas', to: `${base}/ventas`, icon: FileText },
    { key: 'canjes', label: 'Canjes', to: `${base}/canjes`, icon: Gift },
  ].filter((it) => !soloVentasRegistros || TABS_VENTAS_REGISTROS.includes(it.key as never))

  // Determinar el tab activo según la URL
  const activeKey =
    items.find((it) => (it.key === 'resumen' ? location.pathname === base : location.pathname.startsWith(it.to)))?.key ??
    'resumen'

  const c = kpiIconClasses(colorNombre, colorTono)

  return (
    <Tabs
      value={activeKey}
      onValueChange={(v) => {
        const item = items.find((it) => it.key === v)
        if (item) navigate(item.to)
      }}
      className="w-full"
    >
      <TabsList className={`grid w-full h-auto p-1 bg-muted ${GRID_COLS[items.length] ?? 'grid-cols-4'}`}>
        {items.map((item) => {
          const Icon = item.icon
          const isActive = activeKey === item.key
          return (
            <TabsTrigger
              key={item.key}
              value={item.key}
              className="flex items-center gap-2 py-2.5 data-[state=active]:bg-background data-[state=active]:shadow-sm"
            >
              <Icon
                className={`h-4 w-4 ${isActive ? c.icon : 'text-muted-foreground'}`}
              />
              <span className="text-sm font-medium">{item.label}</span>
            </TabsTrigger>
          )
        })}
      </TabsList>
    </Tabs>
  )
}
